// AURA Esdeveniments Externs dictionary (ca / es / en). Keep keys identical across locales.
import { useI18n } from "@/lib/i18n";

export const MOBIL_SERVICE_KEYS = [
  "concept", "catering", "bar", "decor", "furniture", "sound", "lighting", "entertainment", "staff",
];

const ca = {
  name: "AURA Esdeveniments Externs",
  navLabel: "AURA ESDEVENIMENTS EXTERNS",
  badge: "Esdeveniments externs",
  home: { cta: "Descobreix AURA Esdeveniments Externs", ctaSecondary: "Veure disponibilitat" },
  seo: {
    title: "AURA Esdeveniments Externs · A domicili i fora del local | Sant Cugat · Barcelona",
    description:
      "AURA Esdeveniments Externs porta l'experiència Aura al teu espai: masies, jardins, terrasses, oficines o cases particulars. Event management, càtering, barra de còctels, decoració, DJ i so. Consulta disponibilitat i demana pressupost.",
  },
  nav: {
    intro: "Què és", services: "Serveis", gallery: "Galeria", process: "Com funciona",
    calendar: "Disponibilitat", contact: "Contacte", backToSala: "AURA Sala", cta: "Demana pressupost",
  },
  hero: {
    eyebrow: "AURA Esdeveniments Externs · Fora del nostre espai",
    titleA: "Portem l'experiència",
    titleHl: "Aura",
    titleB: "allà on siguis",
    desc: "Masies, jardins, terrasses, oficines o la teva pròpia casa. Dissenyem, muntem i coordinem el teu esdeveniment perquè només t'hagis de preocupar de gaudir-lo.",
    ctaPrimary: "Consultar disponibilitat",
    ctaSecondary: "Descobrir serveis",
  },
  intro: {
    eyebrow: "Què és AURA Esdeveniments Externs",
    title: "La nostra manera de fer esdeveniments, ara al teu espai",
    p1: "AURA Esdeveniments Externs és la línia d'Aura dedicada als esdeveniments fora del nostre local. Portem el mateix criteri estètic, la mateixa cura pel detall i el mateix equip a l'espai que tu triïs.",
    p2: "Tant si tens una localització clara com si encara l'estàs buscant, t'acompanyem des de la primera idea fins a l'últim brindis.",
    pillars: [
      { title: "Gestió integral", desc: "Un sol interlocutor per a tot l'esdeveniment." },
      { title: "Totalment a mida", desc: "Cada proposta es dissenya des de zero." },
      { title: "Qualsevol espai", desc: "Interior, exterior, urbà o rural." },
    ],
  },
  services: {
    eyebrow: "Serveis i possibilitats",
    title: "Tot el que necessites, combinat com vulguis",
    desc: "Tria només el que et cal o deixa-ho tot a les nostres mans. Cada servei s'adapta a l'espai, al nombre de persones i a l'estil del teu esdeveniment.",
    items: {
      concept: { title: "Idea i conceptualització", desc: "Definim el concepte, la temàtica i el fil conductor de l'esdeveniment." },
      catering: { title: "Càtering amb xef privat", desc: "Menús a mida, còctel, showcooking o sopar assegut." },
      bar: { title: "Barra i còctels", desc: "Barra mòbil amb bartenders, coctelleria d'autor i vins seleccionats." },
      decor: { title: "Decoració i ambientació", desc: "Flors, centres de taula, tèxtils i estilisme coherent amb la teva idea." },
      furniture: { title: "Mobiliari", desc: "Taules, cadires, lounge i elements especials per a cada format." },
      sound: { title: "DJ i so", desc: "Equip de so professional, DJ i música en directe." },
      lighting: { title: "Il·luminació", desc: "Llums ambientals, garlandes i il·luminació escènica." },
      entertainment: { title: "Animació", desc: "Activitats, fotomatón, espectacles i experiències per als convidats." },
      staff: { title: "Coordinació i personal", desc: "Coordinador el dia de l'esdeveniment, cambrers i muntatge." },
    },
  },
  gallery: { eyebrow: "Inspiració", title: "Moments fora de l'Aura" },
  process: {
    eyebrow: "Experiència personalitzada",
    title: "Com treballem amb tu",
    desc: "Un procés clar i proper, pensat perquè sàpigues en tot moment en quin punt està el teu esdeveniment.",
    steps: [
      { title: "Ens expliques la teva idea", desc: "Data, espai, nombre de persones i el que t'imagines." },
      { title: "Dissenyem la proposta", desc: "Concepte, serveis i pressupost detallat, sense compromís." },
      { title: "Ho coordinem tot", desc: "Proveïdors, logística, muntatge i timings." },
      { title: "Tu gaudeixes", desc: "El dia de l'esdeveniment el nostre equip s'encarrega de tot." },
    ],
  },
  calendar: {
    eyebrow: "Disponibilitat AURA Esdeveniments Externs",
    title: "Tria el dia del teu esdeveniment",
    desc: "Aquest calendari és independent del de la sala. Podem atendre més d'un esdeveniment mòbil el mateix dia, així que totes les dates futures estan obertes a sol·licitud.",
    hint: "Clica un dia per enviar la teva sol·licitud.",
    legendFree: "Disponible",
    legendMarked: "Amb esdeveniments (encara pots sol·licitar-lo)",
    tooltipFree: "Disponible · clica per sol·licitar",
    tooltipMarked: (n) => `${n} esdeveniment${n > 1 ? "s" : ""} aquest dia · encara pots sol·licitar-lo`,
  },
  form: {
    eyebrow: "Sol·licitud AURA Esdeveniments Externs",
    title: "Parlem del teu esdeveniment",
    desc: "Desem la teva sol·licitud i t'obrim WhatsApp amb un missatge llest perquè et responguem com més aviat millor.",
    name: "Nom", email: "Email", phone: "Telèfon", date: "Data de l'esdeveniment",
    location: "Ubicació / espai", locationPh: "Ex: masia a Matadepera, jardí particular…",
    attendees: "Nombre aproximat de persones", eventType: "Tipus d'esdeveniment",
    eventTypePh: "Tria una opció",
    eventTypes: ["Casament", "Aniversari", "Esdeveniment d'empresa", "Sopar privat", "Celebració familiar", "Altres"],
    services: "Serveis que t'interessen",
    message: "Explica'ns la teva idea (opcional)", messagePh: "Estil, horari, necessitats especials…",
    submit: "Enviar sol·licitud i obrir WhatsApp", submitting: "Enviant…",
    required: "Omple nom, email, telèfon i data.",
    sent: "Sol·licitud enviada. T'obrim WhatsApp…", error: "No s'ha pogut enviar. Torna-ho a provar.",
  },
  contact: {
    eyebrow: "Contacte",
    title: "Fem realitat el teu esdeveniment",
    desc: "Respondrem la teva sol·licitud amb una primera proposta. Si ho prefereixes, escriu-nos directament per WhatsApp.",
    whatsapp: "Escriure per WhatsApp",
    whatsappText: "Hola Aura! Vull informació sobre AURA ESDEVENIMENTS EXTERNS (esdeveniment fora del local).",
  },
};

const es = {
  name: "AURA Eventos Externos",
  navLabel: "AURA EVENTOS EXTERNOS",
  badge: "Eventos externos",
  home: { cta: "Descubre AURA Eventos Externos", ctaSecondary: "Ver disponibilidad" },
  seo: {
    title: "AURA Eventos Externos · A domicilio y fuera del local | Sant Cugat · Barcelona",
    description:
      "AURA Eventos Externos lleva la experiencia Aura a tu espacio: masías, jardines, terrazas, oficinas o casas particulares. Event management, catering, barra de cócteles, decoración, DJ y sonido. Consulta disponibilidad y pide presupuesto.",
  },
  nav: {
    intro: "Qué es", services: "Servicios", gallery: "Galería", process: "Cómo funciona",
    calendar: "Disponibilidad", contact: "Contacto", backToSala: "AURA Sala", cta: "Pide presupuesto",
  },
  hero: {
    eyebrow: "AURA Eventos Externos · Fuera de nuestro espacio",
    titleA: "Llevamos la experiencia",
    titleHl: "Aura",
    titleB: "allá donde estés",
    desc: "Masías, jardines, terrazas, oficinas o tu propia casa. Diseñamos, montamos y coordinamos tu evento para que solo tengas que preocuparte de disfrutarlo.",
    ctaPrimary: "Consultar disponibilidad",
    ctaSecondary: "Descubrir servicios",
  },
  intro: {
    eyebrow: "Qué es AURA Eventos Externos",
    title: "Nuestra manera de hacer eventos, ahora en tu espacio",
    p1: "AURA Eventos Externos es la línea de Aura dedicada a los eventos fuera de nuestro local. Llevamos el mismo criterio estético, el mismo cuidado por el detalle y el mismo equipo al espacio que elijas.",
    p2: "Tanto si tienes una localización clara como si todavía la estás buscando, te acompañamos desde la primera idea hasta el último brindis.",
    pillars: [
      { title: "Gestión integral", desc: "Un único interlocutor para todo el evento." },
      { title: "Totalmente a medida", desc: "Cada propuesta se diseña desde cero." },
      { title: "Cualquier espacio", desc: "Interior, exterior, urbano o rural." },
    ],
  },
  services: {
    eyebrow: "Servicios y posibilidades",
    title: "Todo lo que necesitas, combinado como quieras",
    desc: "Elige solo lo que necesites o déjalo todo en nuestras manos. Cada servicio se adapta al espacio, al número de personas y al estilo de tu evento.",
    items: {
      concept: { title: "Idea y conceptualización", desc: "Definimos el concepto, la temática y el hilo conductor del evento." },
      catering: { title: "Catering con chef privado", desc: "Menús a medida, cóctel, showcooking o cena sentada." },
      bar: { title: "Barra y cócteles", desc: "Barra móvil con bartenders, coctelería de autor y vinos seleccionados." },
      decor: { title: "Decoración y ambientación", desc: "Flores, centros de mesa, textiles y estilismo coherente con tu idea." },
      furniture: { title: "Mobiliario", desc: "Mesas, sillas, lounge y elementos especiales para cada formato." },
      sound: { title: "DJ y sonido", desc: "Equipo de sonido profesional, DJ y música en directo." },
      lighting: { title: "Iluminación", desc: "Luces ambientales, guirnaldas e iluminación escénica." },
      entertainment: { title: "Animación", desc: "Actividades, fotomatón, espectáculos y experiencias para los invitados." },
      staff: { title: "Coordinación y personal", desc: "Coordinador el día del evento, camareros y montaje." },
    },
  },
  gallery: { eyebrow: "Inspiración", title: "Momentos fuera de Aura" },
  process: {
    eyebrow: "Experiencia personalizada",
    title: "Cómo trabajamos contigo",
    desc: "Un proceso claro y cercano, pensado para que sepas en todo momento en qué punto está tu evento.",
    steps: [
      { title: "Nos cuentas tu idea", desc: "Fecha, espacio, número de personas y lo que imaginas." },
      { title: "Diseñamos la propuesta", desc: "Concepto, servicios y presupuesto detallado, sin compromiso." },
      { title: "Lo coordinamos todo", desc: "Proveedores, logística, montaje y timings." },
      { title: "Tú disfrutas", desc: "El día del evento nuestro equipo se encarga de todo." },
    ],
  },
  calendar: {
    eyebrow: "Disponibilidad AURA Eventos Externos",
    title: "Elige el día de tu evento",
    desc: "Este calendario es independiente del de la sala. Podemos atender más de un evento móvil el mismo día, así que todas las fechas futuras están abiertas a solicitud.",
    hint: "Haz clic en un día para enviar tu solicitud.",
    legendFree: "Disponible",
    legendMarked: "Con eventos (aún puedes solicitarlo)",
    tooltipFree: "Disponible · haz clic para solicitar",
    tooltipMarked: (n) => `${n} evento${n > 1 ? "s" : ""} este día · aún puedes solicitarlo`,
  },
  form: {
    eyebrow: "Solicitud AURA Eventos Externos",
    title: "Hablemos de tu evento",
    desc: "Guardamos tu solicitud y te abrimos WhatsApp con un mensaje listo para responderte lo antes posible.",
    name: "Nombre", email: "Email", phone: "Teléfono", date: "Fecha del evento",
    location: "Ubicación / espacio", locationPh: "Ej: masía en Matadepera, jardín particular…",
    attendees: "Número aproximado de personas", eventType: "Tipo de evento",
    eventTypePh: "Elige una opción",
    eventTypes: ["Boda", "Cumpleaños", "Evento de empresa", "Cena privada", "Celebración familiar", "Otros"],
    services: "Servicios que te interesan",
    message: "Cuéntanos tu idea (opcional)", messagePh: "Estilo, horario, necesidades especiales…",
    submit: "Enviar solicitud y abrir WhatsApp", submitting: "Enviando…",
    required: "Rellena nombre, email, teléfono y fecha.",
    sent: "Solicitud enviada. Te abrimos WhatsApp…", error: "No se ha podido enviar. Inténtalo de nuevo.",
  },
  contact: {
    eyebrow: "Contacto",
    title: "Hagamos realidad tu evento",
    desc: "Responderemos tu solicitud con una primera propuesta. Si lo prefieres, escríbenos directamente por WhatsApp.",
    whatsapp: "Escribir por WhatsApp",
    whatsappText: "¡Hola Aura! Quiero información sobre AURA EVENTOS EXTERNOS (evento fuera del local).",
  },
};

const en = {
  name: "AURA External Events",
  navLabel: "AURA EXTERNAL EVENTS",
  badge: "External events",
  home: { cta: "Discover AURA External Events", ctaSecondary: "See availability" },
  seo: {
    title: "AURA External Events · Off-site & at-home events | Sant Cugat · Barcelona",
    description:
      "AURA External Events brings the Aura experience to your space: country houses, gardens, terraces, offices or private homes. Event management, catering, cocktail bar, décor, DJ and sound. Check availability and request a quote.",
  },
  nav: {
    intro: "About", services: "Services", gallery: "Gallery", process: "How it works",
    calendar: "Availability", contact: "Contact", backToSala: "AURA Venue", cta: "Request a quote",
  },
  hero: {
    eyebrow: "AURA External Events · Beyond our venue",
    titleA: "We bring the",
    titleHl: "Aura",
    titleB: "experience wherever you are",
    desc: "Country houses, gardens, terraces, offices or your own home. We design, set up and run your event so all you have to do is enjoy it.",
    ctaPrimary: "Check availability",
    ctaSecondary: "Discover services",
  },
  intro: {
    eyebrow: "What is AURA External Events",
    title: "Our way of hosting events, now in your space",
    p1: "AURA External Events is Aura's line dedicated to events outside our venue. We bring the same aesthetic, the same attention to detail and the same team to the space you choose.",
    p2: "Whether you already have a location or are still looking for one, we're with you from the first idea to the last toast.",
    pillars: [
      { title: "End-to-end management", desc: "One single contact for the whole event." },
      { title: "Fully bespoke", desc: "Every proposal is designed from scratch." },
      { title: "Any space", desc: "Indoor, outdoor, urban or rural." },
    ],
  },
  services: {
    eyebrow: "Services & possibilities",
    title: "Everything you need, combined your way",
    desc: "Pick only what you need or leave it all in our hands. Every service adapts to the space, the number of guests and the style of your event.",
    items: {
      concept: { title: "Idea & concept", desc: "We define the concept, theme and common thread of the event." },
      catering: { title: "Private chef catering", desc: "Bespoke menus, cocktail, showcooking or seated dinner." },
      bar: { title: "Bar & cocktails", desc: "Mobile bar with bartenders, signature cocktails and selected wines." },
      decor: { title: "Décor & styling", desc: "Flowers, centrepieces, textiles and styling that fits your idea." },
      furniture: { title: "Furniture", desc: "Tables, chairs, lounge areas and special pieces for every format." },
      sound: { title: "DJ & sound", desc: "Professional sound system, DJ and live music." },
      lighting: { title: "Lighting", desc: "Ambient lights, festoons and stage lighting." },
      entertainment: { title: "Entertainment", desc: "Activities, photo booth, shows and guest experiences." },
      staff: { title: "Coordination & staff", desc: "On-the-day coordinator, waiters and set-up crew." },
    },
  },
  gallery: { eyebrow: "Inspiration", title: "Moments beyond Aura" },
  process: {
    eyebrow: "Personalised experience",
    title: "How we work with you",
    desc: "A clear, friendly process so you always know where your event stands.",
    steps: [
      { title: "Tell us your idea", desc: "Date, space, number of guests and what you imagine." },
      { title: "We design the proposal", desc: "Concept, services and a detailed, no-obligation quote." },
      { title: "We coordinate everything", desc: "Suppliers, logistics, set-up and timings." },
      { title: "You enjoy", desc: "On the day, our team takes care of everything." },
    ],
  },
  calendar: {
    eyebrow: "AURA External Events availability",
    title: "Pick the day of your event",
    desc: "This calendar is independent from the venue's. We can run more than one mobile event on the same day, so every future date is open for requests.",
    hint: "Click a day to send your request.",
    legendFree: "Available",
    legendMarked: "Has events (you can still request it)",
    tooltipFree: "Available · click to request",
    tooltipMarked: (n) => `${n} event${n > 1 ? "s" : ""} this day · you can still request it`,
  },
  form: {
    eyebrow: "AURA External Events request",
    title: "Let's talk about your event",
    desc: "We save your request and open WhatsApp with a ready-made message so we can get back to you as soon as possible.",
    name: "Name", email: "Email", phone: "Phone", date: "Event date",
    location: "Location / space", locationPh: "E.g. country house in Matadepera, private garden…",
    attendees: "Approximate number of guests", eventType: "Event type",
    eventTypePh: "Choose an option",
    eventTypes: ["Wedding", "Birthday", "Corporate event", "Private dinner", "Family celebration", "Other"],
    services: "Services you're interested in",
    message: "Tell us your idea (optional)", messagePh: "Style, schedule, special requirements…",
    submit: "Send request & open WhatsApp", submitting: "Sending…",
    required: "Please fill in name, email, phone and date.",
    sent: "Request sent. Opening WhatsApp…", error: "Couldn't send. Please try again.",
  },
  contact: {
    eyebrow: "Contact",
    title: "Let's make your event happen",
    desc: "We'll reply to your request with a first proposal. If you prefer, message us directly on WhatsApp.",
    whatsapp: "Message on WhatsApp",
    whatsappText: "Hi Aura! I'd like information about AURA EXTERNAL EVENTS (off-site event).",
  },
};

export const MOBIL_DICT = { ca, es, en };

export const useMobilI18n = () => {
  const { locale } = useI18n();
  return { m: MOBIL_DICT[locale] || ca, locale };
};

// WhatsApp message is always clearly tagged AURA ESDEVENIMENTS EXTERNS so it is distinguishable from venue bookings
export const buildMobilWhatsAppText = (f, dateLabel, serviceLabels) =>
  [
    "*AURA ESDEVENIMENTS EXTERNS* — Sol·licitud d'esdeveniment fora del local",
    `Nom: ${f.name}`,
    `Data: ${dateLabel}`,
    f.location && `Ubicació: ${f.location}`,
    f.attendees && `Persones: ${f.attendees}`,
    f.eventType && `Tipus: ${f.eventType}`,
    serviceLabels.length > 0 && `Serveis: ${serviceLabels.join(", ")}`,
    `Email: ${f.email} · Tel: ${f.phone}`,
    f.message && `Notes: ${f.message}`,
  ].filter(Boolean).join("\n");
